import type {
  ImportIssue,
  ImportedPageDocument,
  WebsiteImportResult,
} from './import-types';

export type ImportReadiness =
  | 'ready'
  | 'warning'
  | 'error';

export type ImportInspection = {
  summary: {
    pages: number;
    assets: number;
    images: number;
    stylesheets: number;
    scripts: number;
    issues: number;
    warnings: number;
    errors: number;
  };
  findings: {
    navigationDetected: boolean;
    footerDetected: boolean;
    forms: number;
    logoCandidates: number;
  };
  readiness: ImportReadiness;
  issues: ImportIssue[];
};

function isImageMimeType(mimeType: string): boolean {
  return mimeType.toLowerCase().startsWith('image/');
}

function isStylesheetMimeType(mimeType: string): boolean {
  return mimeType.toLowerCase() === 'text/css';
}

function isScriptMimeType(mimeType: string): boolean {
  const normalizedMimeType = mimeType.toLowerCase();

  return (
    normalizedMimeType === 'text/javascript' ||
    normalizedMimeType === 'application/javascript'
  );
}

function countMatches(
  source: string,
  pattern: RegExp,
): number {
  return Array.from(source.matchAll(pattern)).length;
}

function pageHasNavigation(
  page: ImportedPageDocument,
): boolean {
  return (
    /<nav\b/i.test(page.sourceDocument) ||
    /\brole\s*=\s*(?:"navigation"|'navigation'|navigation)/i.test(
      page.sourceDocument,
    )
  );
}

function pageHasFooter(
  page: ImportedPageDocument,
): boolean {
  return (
    /<footer\b/i.test(page.sourceDocument) ||
    /\brole\s*=\s*(?:"contentinfo"|'contentinfo'|contentinfo)/i.test(
      page.sourceDocument,
    )
  );
}

function countForms(
  page: ImportedPageDocument,
): number {
  return countMatches(
    page.sourceDocument,
    /<form\b[^>]*>/gi,
  );
}

function isLogoCandidatePath(path: string): boolean {
  return /(?:^|[/_.-])logo(?:[/_.-]|$)/i.test(path);
}

function determineReadiness(
  issues: ImportIssue[],
): ImportReadiness {
  if (
    issues.some((issue) => issue.severity === 'error')
  ) {
    return 'error';
  }

  if (
    issues.some((issue) => issue.severity === 'warning')
  ) {
    return 'warning';
  }

  return 'ready';
}

export function inspectWebsiteImport(
  result: WebsiteImportResult,
): ImportInspection {
  const warnings = result.issues.filter(
    (issue) => issue.severity === 'warning',
  ).length;

  const errors = result.issues.filter(
    (issue) => issue.severity === 'error',
  ).length;

  return {
    summary: {
      pages: result.pages.length,
      assets: result.assets.length,
      images: result.assets.filter((asset) =>
        isImageMimeType(asset.mimeType),
      ).length,
      stylesheets: result.assets.filter((asset) =>
        isStylesheetMimeType(asset.mimeType),
      ).length,
      scripts: result.assets.filter((asset) =>
        isScriptMimeType(asset.mimeType),
      ).length,
      issues: result.issues.length,
      warnings,
      errors,
    },

    findings: {
      navigationDetected: result.pages.some(
        pageHasNavigation,
      ),
      footerDetected: result.pages.some(
        pageHasFooter,
      ),
      forms: result.pages.reduce(
        (total, page) => total + countForms(page),
        0,
      ),
      logoCandidates: result.assets.filter(
        (asset) =>
          isImageMimeType(asset.mimeType) &&
          isLogoCandidatePath(asset.normalizedPath),
      ).length,
    },

    readiness: determineReadiness(result.issues),

    issues: [...result.issues],
  };
}
